import { useEffect, useRef, useState } from "react";
import type { Control, UseFormSetValue } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { IsLoading } from "src/common/components/AppState/IsLoading";
import { SearchInput } from "src/common/components/SearchInput";
import { store } from "src/common/state/store";
import ModalConfirmation from "../../../common/components/Modal/ModalConfirmation";
import { useAppDispatch, useAppSelector } from "../../../common/state/hooks";
import { closeConfirmationModal } from "../../../common/state/slice/modal/modalSlice";
import type { IProject, IProjectTag } from "../../../model/model";
import type { INewProjectFormValues } from "../state/projectsSlice";
import {
  deleteProject,
  getCurrentTemplateId,
  setCurrentTemplateId,
  TemplatesForTagSelector,
} from "../state/projectsSlice";
import Spinner from "src/common/components/Loader/Spinner";
import TemplateCards from "./TemplateCards";

const TemplateCategories = ({
  tagList,
  control,
  setValue,
  insideOfProject = true,
}: {
  setValue?: UseFormSetValue<INewProjectFormValues>;
  control?: Control<INewProjectFormValues, object>;
  tagList: IProjectTag[];
  insideOfProject?: boolean;
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const currentTemplateId = useAppSelector(getCurrentTemplateId);
  const [sortedTaglist, setSortedTaglist] = useState<IProjectTag[]>([]);

  useEffect(() => {
    setSortedTaglist(
      tagList
        .filter((tag: IProjectTag) => tag.projects.filter((p) => !p.is_deleted).length >= 1)
        .sort((a: any, b: any) => b.id - a.id),
    );
  }, [tagList]);

  const [collapsedCategories, setCollapsedCategories] = useState<string[]>([]);
  const [templatesByTag, setTemplatesByTag] = useState<Record<string, IProject[]>>({});
  const templatesContainerRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const [filteredTemplatesByTag, setFilteredTemplatesByTag] = useState<Record<string, IProject[]>>(
    {},
  );

  const [isComputingTemplates, setIsComputingTemplates] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [tagsWithCollapseButton, setTagsWithCollapseButton] = useState<Set<string>>(new Set());

  const handleToggleCollapse = (category: string) => {
    setCollapsedCategories((prevCollapsedCategories) =>
      prevCollapsedCategories.includes(category)
        ? prevCollapsedCategories.filter((t) => t !== category)
        : [...prevCollapsedCategories, category],
    );
  };

  const itemHeight = 146;
  const maxVisibleHeight = 2 * itemHeight;

  const checkContainerHeight = (tag: string) => {
    const containerRef = templatesContainerRefs.current[tag];
    if (containerRef) {
      // Use scrollHeight to detect overflow even when maxHeight is applied
      const scrollHeight = containerRef.scrollHeight;
      return scrollHeight > maxVisibleHeight;
    }
    return false;
  };

  useEffect(() => {
    const initialCollapsedCategories = tagList.map((tag) => tag.slug);
    setCollapsedCategories(initialCollapsedCategories);
  }, []);

  const filterTemplates = (
    templatesToFilter: Record<string, IProject[]>,
    query: string = searchQuery,
  ) => {
    const normalizedQuery = query.toLowerCase().trim();

    const newFilteredTemplatesByTag: Record<string, IProject[]> = {};
    Object.keys(templatesToFilter).forEach((tagSlug) => {
      const templates = templatesToFilter[tagSlug] || [];
      newFilteredTemplatesByTag[tagSlug] = normalizedQuery
        ? templates.filter((template) => template.name.toLowerCase().includes(normalizedQuery))
        : templates;
    });

    setFilteredTemplatesByTag(newFilteredTemplatesByTag);

    if (isComputingTemplates) {
      const firstTemplate = Object.values(newFilteredTemplatesByTag)
        .flat()
        .find((template) => !template.is_deleted);
      if (firstTemplate) {
        dispatch(setCurrentTemplateId(firstTemplate.id));
      }
    }
    setIsComputingTemplates(false);
  };

  useEffect(() => {
    const newTemplatesByTag: Record<string, IProject[]> = {};
    sortedTaglist.forEach((tag) => {
      const templates = TemplatesForTagSelector({ tag: tag.slug, state: store.getState() });
      newTemplatesByTag[tag.slug] = templates.filteredProjects;
    });

    setTemplatesByTag(newTemplatesByTag);

    filterTemplates(newTemplatesByTag);

    // Compute which tags should show collapse button after initial render
    // Using setTimeout to wait for DOM to be rendered
    setTimeout(() => {
      const newTagsWithCollapseButton = new Set<string>();
      sortedTaglist.forEach((tag) => {
        if (checkContainerHeight(tag.slug)) {
          newTagsWithCollapseButton.add(tag.slug);
        }
      });
      setTagsWithCollapseButton(newTagsWithCollapseButton);
    }, 100);
  }, [sortedTaglist]);

  // Re-filter when search query changes
  useEffect(() => {
    if (Object.keys(templatesByTag).length > 0) {
      filterTemplates(templatesByTag, searchQuery);
    }
  }, [searchQuery]);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  return (
    <div className="w-full h-full">
      <IsLoading componentId={"projectListComponent"} showSuccess={false} spinnerPlaceholder>
        {isComputingTemplates ? (
          <div className="flex justify-center items-center h-full">
            <Spinner relative={true} />
          </div>
        ) : (
          <div className="flex flex-col grow w-full justify-between h-full">
            <div className="whitespace-no-wrap w-full space-y-6 py-6 px-6">
              {/* Search bar */}
              <div className="flex items-center">
                <SearchInput
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder={`${t("general.search")}...`}
                  className="w-[15rem]"
                />
              </div>

              {sortedTaglist.map((tag: IProjectTag, index: number) => {
                const filteredTemplates = filteredTemplatesByTag[tag.slug] || [];
                const templates = templatesByTag[tag.slug] || [];

                const isCollapsed = collapsedCategories.includes(tag.slug);
                const showCollapseButton = tagsWithCollapseButton.has(tag.slug);

                return (
                  <div key={index}>
                    <div className="flex mb-5 mt-6">
                      <h3 className="font-medium text-lg">{t(`chooseTemplateType.${tag.slug}`)}</h3>
                      {showCollapseButton && (
                        <div className="flex ml-4">
                          <button
                            className="text-green-500 text-sm"
                            onClick={(e) => {
                              e.preventDefault();
                              handleToggleCollapse(tag.slug);
                            }}
                          >
                            {isCollapsed ? t("general.showMore") : t("general.showLess")}
                          </button>
                        </div>
                      )}
                    </div>
                    {templates.length < 1 ? (
                      tag.slug === "custom-templates" ? (
                        <p className="w-96">{t("chooseTemplateType.noCustomTemplate")}</p>
                      ) : (
                        <p className="w-96">{t("pages.templates.notFound")}</p>
                      )
                    ) : filteredTemplates.length < 1 ? (
                      <p className="w-96">{t("pages.templates.notFound")}</p>
                    ) : (
                      <div className="flex justify-start">
                        <div className="flex flex-col grow w-full justify-between h-full">
                          <div className="whitespace-no-wrap w-full space-y-6">
                            <div className="w-full overflow-x-auto">
                              <div
                                ref={(el) => (templatesContainerRefs.current[tag.slug] = el)}
                                className="grid gap-6 sm:gap-5 w-auto personalize-scroll-visible py-2 px-3"
                                style={{
                                  gridTemplateColumns:
                                    "repeat(auto-fit, minmax(224px, max-content))",
                                  maxHeight: isCollapsed
                                    ? `calc(2 * ${itemHeight}px + 0.1px)`
                                    : "none",
                                  overflowY: "hidden",
                                }}
                              >
                                <TemplateCards
                                  templates={filteredTemplates}
                                  tagSlug={tag.slug}
                                  control={control}
                                  setValue={setValue}
                                  insideOfProject={insideOfProject}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              <ModalConfirmation
                id="deleteTemplateConfirmation"
                onClickSubmit={() => {
                  dispatch(closeConfirmationModal("deleteTemplateConfirmation"));
                  void dispatch(
                    deleteProject({
                      id: currentTemplateId,
                      componentId: `template-${currentTemplateId}`,
                    }),
                  );

                  setIsComputingTemplates(true);
                }}
                title={t("pages.faitTemplate.deletionModal.title")}
                text={t("pages.faitTemplate.deletionModal.body")}
                submitButtonText={t("general.delete")}
              />
            </div>
          </div>
        )}
      </IsLoading>
    </div>
  );
};

export default TemplateCategories;
